import { Injectable } from '@angular/core'

/** O pdf.js e os componentes do visualizador, carregados dos assets do app. */
export interface PdfJs {
	/** `pdfjs-dist/build/pdf.min.mjs`: `getDocument`, `GlobalWorkerOptions`… */
	lib: any
	/** `pdfjs-dist/web/pdf_viewer.mjs`: `PDFViewer`, `EventBus`, `PDFLinkService`… */
	viewer: any
}

/**
 * Carrega o pdf.js (versão fixada no `package.json`) dos assets do app, em `/pdfjs/` (`angular.json`), sem CDN e fora
 * do bundle: só quem abre o livro baixa os ~2 MB da biblioteca e do worker. O import é por URL, em tempo de execução,
 * porque o build do pdf.js 4 usa recursos de módulo que o bundler do Angular com zone.js não aceita.
 *
 * O `pdf_viewer.mjs` lê a biblioteca de `globalThis.pdfjsLib` ao carregar: a ordem importa.
 */
@Injectable({ providedIn: 'root' })
export class PdfJsLoaderService {
	/** Onde estão os arquivos do pdf.js. Nos testes, o Karma serve o build em outro caminho. */
	baseUrl = 'pdfjs/'

	private loading?: Promise<PdfJs>

	load(): Promise<PdfJs> {
		this.loading ??= this.doLoad().catch(error => {
			// Uma falha de rede não pode prender a tela: a próxima abertura tenta de novo.
			this.loading = undefined
			throw error
		})

		return this.loading
	}

	private async doLoad(): Promise<PdfJs> {
		const base = new URL(this.baseUrl, document.baseURI).href
		const libUrl = `${base}pdf.min.mjs`
		const viewerUrl = `${base}pdf_viewer.mjs`

		const lib = await import(/* webpackIgnore: true */ /* @vite-ignore */ libUrl)
		lib.GlobalWorkerOptions.workerSrc = `${base}pdf.worker.min.mjs`
		;(globalThis as any).pdfjsLib = lib

		const viewer = await import(/* webpackIgnore: true */ /* @vite-ignore */ viewerUrl)
		this.ensureStylesheet(`${base}pdf_viewer.css`)

		return { lib, viewer }
	}

	private ensureStylesheet(href: string): void {
		if (document.querySelector(`link[data-pdfjs-viewer]`)) return

		const link = document.createElement('link')
		link.rel = 'stylesheet'
		link.href = href
		link.setAttribute('data-pdfjs-viewer', '')
		document.head.appendChild(link)
	}
}
